import type { Transition } from "@uirouter/react";
import queryString from "query-string";

import { setCountryFilterData } from "app/action_types/country_filter_action-types";
import { safeJsonParse } from "app/UtilitiesAndConstants/UtilityFunctions/safeJsonParse";
import { apiHelper } from "app/common/services/apiHelper";
import type { transitionHookFn } from "app/routes/handlers/redirectFunctions/redirectPipe";
import { DefaultFetchService } from "app/services/fetchService";
import { SwRedux } from "app/services/SwRedux";

const getUpdatedFilterValue = (filterValue: string, sites: string[]): string | null | undefined => {
    if (!filterValue) {
        return;
    }

    const filterValues = filterValue.split(",");

    const validFilterValues = filterValues.filter((value: string) => {
        const [domain] = value.split(":");
        return sites.includes(domain);
    });

    if (validFilterValues.length !== filterValues.length) {
        return validFilterValues.length > 0 ? validFilterValues.join(",") : null;
    }

    return filterValue;
};

const getSites = (pageFilter: string) => {
    const urls = safeJsonParse<{ url: string }[]>(pageFilter, []);
    return urls.map((item) => item.url);
};

export const onRedirectRankingDistribution: transitionHookFn = async (transition: Transition) => {
    // SIM-35217
    // this resolve function is used to redirect the user to the
    // country with the most ranking data, which is resolved from a backend call
    const params = { ...transition.params() };
    const paramsChanges = { ...transition.paramsChanged() };

    const pageName = "organicsearch_website_search_ranking_distribution_v2";
    const countryCode = Number(params.country);
    const countryFilterRedux = SwRedux.getInstance().getState().countryFilter;
    const sites = getSites(params.pageFilter);

    const rankingFilter = getUpdatedFilterValue(params.ranking, sites);
    const positionTrendsFilter = getUpdatedFilterValue(params.positionTrends, sites);

    const filtered = Object.fromEntries(Object.entries(params).filter(([_key, value]) => value));
    const paramsForApi = apiHelper.transformParamsForAPI(filtered);
    const { key, ...rest } = paramsForApi;
    const updatedParamsForApi = { ...rest, keys: key };

    const updatedQueryStringParams = queryString.stringify(updatedParamsForApi);

    const shouldFetchData =
        (paramsChanges?.duration !== undefined ||
            paramsChanges?.webSource !== undefined ||
            paramsChanges?.key !== undefined ||
            countryFilterRedux.countriesData === null) &&
        params.key !== undefined;
    if (shouldFetchData) {
        try {
            const countriesRank = await DefaultFetchService.getInstance().get(
                `/widgetApi/RankDistributionV2/Countries?${updatedQueryStringParams}`,
            );
            SwRedux.getInstance().dispatch(setCountryFilterData(countriesRank));
            const asArray = Object.entries(countriesRank as Record<string, number>);
            const asSortedArray = asArray.sort((a, b) => {
                return a[1] >= b[1] ? -1 : 1;
            });
            if (countryCode === 999) {
                return {
                    state: pageName,
                    params: {
                        ...params,
                        country:
                            Number(asSortedArray[0][0]) === 999
                                ? Number(asSortedArray[1][0])
                                : Number(asSortedArray[0][0]),
                    },
                };
            } else if (rankingFilter !== params.ranking || positionTrendsFilter !== params.positionTrends) {
                return {
                    state: pageName,
                    params: {
                        ...params,
                        ranking: rankingFilter,
                        positionTrends: positionTrendsFilter,
                    },
                };
            }
        } catch (e) {
            return;
        }
    }
};
